import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";

interface ListingProblem {
  restaurantId: number;
  name: string;
  code: string;
}

/**
 * Tells the owner, on every page, that Google sync has stopped (G6,
 * GOOGLE_SYNC_PLAN.md): either their whole Google connection was refused, or
 * Google refuses one linked listing. They were emailed once; this stays until
 * it is fixed. Not dismissible, same reasoning as ReadOnlyBanner: it is the
 * current state, not a notification. Admins only, since only the owner can
 * reconnect or relink.
 */
const GoogleAccessBanner: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isAdmin = (JSON.parse(localStorage.getItem("user") || "{}") as any).role === "admin";
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [problems, setProblems] = useState<ListingProblem[]>([]);

  // Re-checked on each page change, so the banner goes away as soon as the
  // owner has reconnected or relinked on the Restaurant page.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    apiFetch("/api/google/status")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setNeedsReconnect(Boolean(data.needsReconnect));
        setProblems(Array.isArray(data.listingProblems) ? data.listingProblems : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAdmin, pathname]);

  if (!isAdmin || (!needsReconnect && !problems.length)) return null;

  let title: string;
  let body: string;
  let to = "/restaurant";
  if (needsReconnect) {
    title = t("googleAccess.reconnectTitle");
    body = t("googleAccess.reconnectBody");
  } else if (problems.length === 1) {
    title = t("googleAccess.listingTitle", { name: problems[0].name });
    body = t(`googleAccess.listingBody.${problems[0].code}`, { defaultValue: t("googleAccess.listingBodyDefault") });
    to = `/restaurant?restaurantId=${problems[0].restaurantId}`;
  } else {
    title = t("googleAccess.listingTitleMany", { count: problems.length });
    body = t("googleAccess.listingBodyDefault");
  }

  return (
    <div role="status" className="bg-red-50 border-b border-red-200 px-4 py-3 text-sm text-red-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-semibold">{title}</span> <span>{body}</span>
        </div>
        {pathname !== "/restaurant" && (
          <Link
            to={to}
            className="shrink-0 px-3 py-1.5 bg-red-600 text-white rounded font-semibold hover:bg-red-700 transition"
          >
            {t("googleAccess.cta")}
          </Link>
        )}
      </div>
    </div>
  );
};

export default GoogleAccessBanner;
